import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import ProjectApi from "../../../../../api/projectApi";
import UsersApi from "../../../../../api/usersApi";
import { ReactComponent as CloseIcon } from "../../../../../assets/General/close.svg";
import CircleAvatar from "../../../../../components/CircleAvatar/CircleAvatar";
import Input from "../../../../../components/Input/Input";
import Loader from "../../../../../components/Loader/Loader";
import ModalWindow from "../../../../../components/ModalWindow/ModalWindow";
import MultipleSelect from "../../../../../components/MultipleSelect/MultipleSelect";
import StandartButton from "../../../../../components/StandartButton/StandartButton";
import { ContextProvider } from "../../../../../contextProvider";
import { ProjectTeamRole } from "../../../../../types/Projects";
import { TypeUserData } from "../../../../../types/TypeUsers";
import { getTranslation } from "../../../../../utils/getTranslation";
import Notify, { SuccesNotify } from "../../../../../utils/toaster";
import { teamRoleOptions } from "./programInfoUtils";
import s from "./ProgramInfo.module.css";

type SelectedTeamInvite = {
  user: TypeUserData;
  roles: ProjectTeamRole[];
};

type TeamInviteModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  projectId: string;
  existingTeamUserIds: string[];
  onInvitesSent: () => Promise<void>;
};

const getUserName = (user: TypeUserData) =>
  `${getTranslation(user.name, "en")} ${getTranslation(user.surname, "en")}`.trim() ||
  user.username ||
  "-";

const TeamInviteModal = ({
  isOpen,
  setIsOpen,
  projectId,
  existingTeamUserIds,
  onInvitesSent,
}: TeamInviteModalProps) => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<TypeUserData[]>([]);
  const [selectedInvites, setSelectedInvites] = useState<SelectedTeamInvite[]>(
    [],
  );
  const [isSearching, setIsSearching] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const selectedUserIds = useMemo(
    () => new Set(selectedInvites.map((invite) => invite.user._id)),
    [selectedInvites],
  );

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setUsers([]);
      setSelectedInvites([]);
      return;
    }

    if (!token || search.trim().length < 2) {
      setUsers([]);
      return;
    }

    const timeout = window.setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await UsersApi.findUserBySearchString(
          token,
          search.trim(),
          "false",
        );

        if (response.status && response.users) {
          setUsers(response.users);
        } else {
          setUsers([]);
        }
      } catch (error) {
        console.error("Error searching project team users:", error);
        setUsers([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [isOpen, search, token]);

  const addUser = (user: TypeUserData) => {
    if (!user.shortId) return;

    setSelectedInvites((prev) => [
      ...prev,
      {
        user,
        roles: [ProjectTeamRole.MANAGER],
      },
    ]);
  };

  const removeUser = (userId: string) => {
    setSelectedInvites((prev) =>
      prev.filter((invite) => invite.user._id !== userId),
    );
  };

  const updateRoles = (userId: string, roles: string | string[]) => {
    const nextRoles = Array.isArray(roles) ? roles : [roles];

    setSelectedInvites((prev) =>
      prev.map((invite) =>
        invite.user._id === userId
          ? { ...invite, roles: nextRoles as ProjectTeamRole[] }
          : invite,
      ),
    );
  };

  const sendInvites = async () => {
    if (!token || !userData) return;

    if (!selectedInvites.length) {
      Notify("Select at least one team member");
      return;
    }

    if (selectedInvites.some((invite) => !invite.roles.length)) {
      Notify("Select at least one role for every team member");
      return;
    }

    setIsSending(true);
    try {
      const response = await ProjectApi.inviteTeamMembers(token, {
        projectId,
        invitedBy: userData._id,
        invitations: selectedInvites.map((invite) => ({
          shortId: invite.user.shortId,
          roles: invite.roles,
        })),
      });

      if (response.status) {
        SuccesNotify("Team invitations sent");
        setIsOpen(false);
        await onInvitesSent();
      } else {
        Notify(response.message ?? "Failed to invite team members");
      }
    } catch (error) {
      console.error("Error inviting project team members:", error);
      Notify("Failed to invite team members");
    } finally {
      setIsSending(false);
    }
  };

  const canSend =
    selectedInvites.length > 0 &&
    selectedInvites.every((invite) => invite.roles.length > 0) &&
    !isSending;

  return (
    <ModalWindow
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      width={window.innerWidth > 900 ? "820px" : "90svw"}
      padding="20px"
      title="Invite project team members"
    >
      {isSending ? (
        <div className={s.teamModalLoader}>
          <Loader size={80} />
        </div>
      ) : (
        <div className={s.teamInviteForm}>
          <div className={s.sectionTitle}>Selected ({selectedInvites.length})</div>
          <div className={s.teamSelectedList}>
            {selectedInvites.length ? (
              selectedInvites.map((invite) => (
                <div key={invite.user._id} className={s.teamSelectedCard}>
                  <div className={s.teamUserInfo}>
                    <CircleAvatar userId={invite.user._id} width="44px" height="44px" />
                    <div className={s.teamUserText}>
                      <div className={s.name}>{getUserName(invite.user)}</div>
                      <div className={s.email}>{invite.user.email}</div>
                      <div className={s.email}>Short ID: {invite.user.shortId}</div>
                    </div>
                  </div>
                  <div className={s.teamRoleSelect}>
                    <MultipleSelect
                      data={teamRoleOptions}
                      value={invite.roles}
                      setValue={(value) => updateRoles(invite.user._id, value)}
                      multiplie
                      checkBox
                      chip
                      width="100%"
                      lable="Roles"
                      bgColor="rgb(34, 34, 34)"
                      border={
                        invite.roles.length
                          ? "1px solid rgba(255, 255, 255, 0.12)"
                          : "1px solid var(--main-red)"
                      }
                    />
                  </div>
                  <CloseIcon
                    className={s.closeIcon}
                    onClick={() => removeUser(invite.user._id)}
                  />
                </div>
              ))
            ) : (
              <div className={s.empty}>No team members selected</div>
            )}
          </div>

          <div className={s.sectionTitle}>Find users</div>
          <Input
            inputValue={search}
            onChangeInput={setSearch}
            placeholder="Search by name or email..."
            isSearchinput={!search}
            isCloseIcon={!!search}
            closeFunction={() => setSearch("")}
            bgColor="rgb(34, 34, 34)"
          />

          <div className={s.teamSearchList}>
            {isSearching && <div className={s.loading}>Searching...</div>}
            {!isSearching &&
              users.map((user) => {
                const alreadySelected = selectedUserIds.has(user._id);
                const alreadyInTeam = existingTeamUserIds.includes(user._id);
                const isExpert = Boolean(user.specialistIds?.length);
                const canAdd =
                  Boolean(user.shortId) &&
                  !alreadySelected &&
                  !alreadyInTeam &&
                  !isExpert;

                return (
                  <div key={user._id} className={s.teamSearchCard}>
                    <CircleAvatar userId={user._id} width="44px" height="44px" />
                    <div className={s.teamUserInfo}>
                      <div className={s.name}>{getUserName(user)}</div>
                      <div className={s.email}>{user.email}</div>
                      <div className={s.email}>Short ID: {user.shortId || "-"}</div>
                    </div>
                    <button
                      type="button"
                      className={s.addTeamUserBtn}
                      onClick={() => addUser(user)}
                      disabled={!canAdd}
                    >
                      {alreadyInTeam
                        ? "In team"
                        : alreadySelected
                          ? "Added"
                          : isExpert
                            ? "Expert"
                            : user.shortId
                              ? "Add"
                              : "No short ID"}
                    </button>
                  </div>
                );
              })}
            {!isSearching && search.trim().length >= 2 && users.length === 0 && (
              <div className={s.empty}>No users found</div>
            )}
            {search.trim().length < 2 && (
              <div className={s.empty}>Enter at least 2 characters</div>
            )}
          </div>

          <div className={s.teamModalActions}>
            <StandartButton
              buttonTitle="Send invites"
              action={sendInvites}
              height="40px"
              disabled={!canSend}
              tooltipTitle={
                !canSend ? "Select users and roles before sending" : undefined
              }
            />
            <button
              type="button"
              className={s.cancelTeamBtn}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </ModalWindow>
  );
};

export default TeamInviteModal;
